if (!customElements.get('nytc-card-purchase')) {
  customElements.define('nytc-card-purchase', class extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      const form = this.querySelector('form');
      if (!form || !window.theme?.AjaxProduct) return;
      this.bound = true;
      const button = form.querySelector('.nytc-card-buy');
      const status = form.querySelector('[data-card-status]');
      const swatchSelect = form.querySelector('select[data-swatch-select]');
      if (swatchSelect) this.buildSwatchDropdown(form, swatchSelect, status);
      new window.theme.AjaxProduct(form, '.nytc-card-buy');
      form.addEventListener('nytc:cart-attempt', () => {
        button.disabled = true;
        form.setAttribute('aria-busy', 'true');
        status.textContent = '';
      });
      const finish = () => {
        button.disabled = false;
        button.dataset.nytcSource = 'product-card';
        form.removeAttribute('aria-busy');
      };
      form.addEventListener('nytc:cart-confirmed', () => {
        finish();
        status.textContent = status.dataset.addedMessage;
      });
      form.addEventListener('nytc:cart-failed', finish);
      form.addEventListener('ajaxProduct:error', event => {
        finish();
        const message = event.detail?.errorMessage;
        status.textContent = typeof message === 'string' ? message : '';
      });
    }

    /* Oct 21 2026 (Claude): color dropdown that shows the swatch next to each name.
       The native <select name="id"> stays in the form (hidden) and still carries the
       chosen variant, so the add-to-cart request is unchanged. */
    buildSwatchDropdown(form, select, status) {
      const options = [...select.options].filter(o => o.value);
      if (!options.length) return;
      const wrap = document.createElement('div');
      wrap.className = 'nytc-card-dd';
      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'nytc-card-dd__btn';
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      const list = document.createElement('ul');
      list.className = 'nytc-card-dd__list';
      list.setAttribute('role', 'listbox');
      list.hidden = true;
      const circle = o => {
        const c = document.createElement('span');
        c.className = 'nytc-card-dd__swatch';
        c.setAttribute('aria-hidden', 'true');
        if (o.dataset.swatch) c.style.backgroundColor = o.dataset.swatch;
        if (o.dataset.swatchImg) c.style.backgroundImage = 'url(' + o.dataset.swatchImg + ')';
        return c;
      };
      const items = options.map(o => {
        const li = document.createElement('li');
        li.className = 'nytc-card-dd__item';
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', 'false');
        li.dataset.value = o.value;
        li.tabIndex = -1;
        if (o.disabled) { li.classList.add('is-soldout'); li.setAttribute('aria-disabled', 'true'); }
        li.appendChild(circle(o));
        const name = document.createElement('span');
        name.className = 'nytc-card-dd__name';
        name.textContent = o.dataset.name || o.textContent.trim();
        li.appendChild(name);
        const price = document.createElement('span');
        price.className = 'nytc-card-dd__price';
        price.textContent = o.disabled ? (o.textContent.split('—').pop() || '').trim() : (o.dataset.price || '');
        li.appendChild(price);
        list.appendChild(li);
        return li;
      });
      const paint = () => {
        const chosen = options.find(o => o.value === select.value && select.value);
        trigger.innerHTML = '';
        if (chosen) {
          trigger.appendChild(circle(chosen));
          const t = document.createElement('span');
          t.className = 'nytc-card-dd__label';
          t.textContent = chosen.dataset.name || chosen.textContent.trim();
          trigger.appendChild(t);
          const p = document.createElement('span');
          p.className = 'nytc-card-dd__price';
          p.textContent = chosen.dataset.price || '';
          trigger.appendChild(p);
        } else {
          const t = document.createElement('span');
          t.className = 'nytc-card-dd__label nytc-card-dd__label--empty';
          t.textContent = select.dataset.choose || 'Choose a color';
          trigger.appendChild(t);
        }
        items.forEach(li => li.setAttribute('aria-selected', String(li.dataset.value === select.value)));
        wrap.classList.remove('is-missing');
      };
      const open = focusIndex => {
        list.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        wrap.classList.add('is-open');
        const idx = focusIndex ?? Math.max(0, items.findIndex(li => li.dataset.value === select.value));
        items[idx]?.focus();
      };
      const close = refocus => {
        list.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
        wrap.classList.remove('is-open');
        if (refocus) trigger.focus();
      };
      const choose = li => {
        if (li.classList.contains('is-soldout')) return;
        select.value = li.dataset.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        paint();
        close(true);
      };
      trigger.addEventListener('click', () => list.hidden ? open() : close());
      trigger.addEventListener('keydown', e => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); open(); }
      });
      list.addEventListener('click', e => { const li = e.target.closest('.nytc-card-dd__item'); if (li) choose(li); });
      list.addEventListener('keydown', e => {
        const i = items.indexOf(document.activeElement);
        if (e.key === 'ArrowDown') { e.preventDefault(); items[Math.min(items.length - 1, i + 1)]?.focus(); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); items[Math.max(0, i - 1)]?.focus(); }
        else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (i > -1) choose(items[i]); }
        else if (e.key === 'Escape' || e.key === 'Tab') { close(e.key === 'Escape'); }
      });
      document.addEventListener('click', e => { if (!wrap.contains(e.target) && !list.hidden) close(); });
      /* No choice yet: stop the submit here (the native select is hidden, so the browser's
         own "please select" bubble would point at nothing) and open the list instead. */
      form.addEventListener('submit', e => {
        if (select.value) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        wrap.classList.add('is-missing');
        status.textContent = select.dataset.choose || '';
        open(0);
      }, true);
      select.removeAttribute('required');
      select.classList.add('nytc-card-dd__native');
      select.tabIndex = -1;
      select.setAttribute('aria-hidden', 'true');
      wrap.appendChild(trigger);
      wrap.appendChild(list);
      /* Outside the <label>, so a tap on the list can never forward to the hidden native select. */
      (select.closest('label') || select).insertAdjacentElement('afterend', wrap);
      paint();
    }
  });
}
